import React, { ReactNode, FC, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { useCheckVaultMarginAccount } from './hooks/useCheckVaultMarginAccount';
import { useManageDetailsStyle } from './ManageDetails.style';

import { Store } from '@/shared/store';
import { useFetchVaultPools } from '@/shared/hooks/useFetchVaultLinkedPools';
import { useFetchVaults } from '@/shared/hooks/useFetchVaults';

type ManageDetailsVisibilityWrapperProps = {
    children: ReactNode;
};

const ManageDetailsVisibilityWrapper: FC<ManageDetailsVisibilityWrapperProps> = ({ children }) => {
    const classes = useManageDetailsStyle();
    const { address } = useParams();

    const isModalOpen = useSelector((state: Store) => Boolean(state.modals?.activeModal));

    const verificationComplete = useCheckVaultMarginAccount({ address });

    const isNotReady = !verificationComplete;

    return (
        <div className={isNotReady && isModalOpen ? classes.blur : ''}>
            <ManageDetailsWrapper verificationComplete={!!verificationComplete}>
                {children}
            </ManageDetailsWrapper>
        </div>
    );
};

type ManageDetailsWrapperProps = {
    verificationComplete: boolean;
    children: ReactNode;
};

const ManageDetailsWrapper: FC<ManageDetailsWrapperProps> = React.memo(
    ({ children, verificationComplete }) => {
        const { marginAccounts } = useSelector((state: Store) => ({
            marginAccounts: state.userVaults.marginAccounts,
        }));

        const { address } = useParams();

        const currentAccount = useMemo(
            () => marginAccounts?.find((account) => account.agreementAddress === address),
            [marginAccounts, address],
        );

        const { fetchVaultPools } = useFetchVaultPools({
            vaultAddress: address as string,
            marginAccountAddress: currentAccount?.address as string,
        });

        const { fetchVaults } = useFetchVaults();

        useEffect(() => {
            if (verificationComplete) {
                fetchVaults();
                fetchVaultPools();
            }
        }, [verificationComplete]);

        return children;
    },
);

export default ManageDetailsVisibilityWrapper;
